import { useCallback, useMemo, useState } from 'react';

import { useEstimationItemQueryActions } from '@/api/hooks/estimationItemQueryActions';
import type {
  CreateEstimationItemRequest,
  UpdateEstimationItemWithIdRequest,
} from '@/api/schema/data-contracts';
import { ApiEstimationItemPurpose, ApiEstimationStage } from '@/api/schema/data-contracts';
import { AssumptionsModal } from '@/components/Assumption';
import { Loader } from '@/components/atoms/Loader';
import { CustomEstimationItemCreationModal } from '@/components/molecules/CustomEstimationItemCreationModal';
import Search from '@/components/molecules/Search';
import { CommentsModal } from '@/components/organism/Comments/CommentsModal';
import { ExportButtonsSelector } from '@/components/organism/DataTable/components/exportSelector';
import { eExportFormat } from '@/components/organism/DataTable/model';
import type { EstimationExportButtonsProps } from '@/components/organism/EstimationExportButtons';
import { messages as sidebarMessageDefinitions } from '@/components/organism/Sidebar/messages';
import { useToast } from '@/context/toast';
import { useMessages } from '@/hooks/messages';
import { useEstimationExport } from '@/hooks/useEstimationExport';
import { useHasEstimationPermission } from '@/hooks/useHasEstimationPermission';
import EstimationItemDetailModal from '@/pages/estimation/detail/EstimationItemDetailModal';
import type { EstimationItemsGroupedTableProps } from '@/pages/estimation/detail/EstimationItemsGroupedTables';
import { Route } from '@/routes/estimation/detail/$estimationId/$tabId';
import type { OmitSafely } from '@/types';
import { isTemporaryId } from '@/utils/generators';

import { Layout } from '../../Layout';
import { getResourceEstimationItemDetail } from '../../mappers/estimationItemDetailMapper';
import * as estimationItemUpdateRequestMapper from '../../mappers/estimationItemUpdateRequestMapper';
import { estimationTabMessageDefinitions } from '../../messages';
import type {
  EstimationGroupActionCallback,
  EstimationItemActionCallback,
  EstimationItemsEditFormModel,
  ResourceEstimationItemsTableRecord,
} from '../../model';
import { useResourceEstimationItemsTableRecords } from '../../useEstimationItemsTableRecords';
import { prepareRecalculate } from './calculationHelper';
import { TopLevelEstimationItemsForm } from './TopLevelEstimationItemsForm';

const emptyArray: Array<ResourceEstimationItemsTableRecord> = [];

const EstimationDetailTab = () => {
  const { estimationId } = Route.useLoaderData();
  const sidebarMessages = useMessages(sidebarMessageDefinitions);
  const tabMessages = useMessages(estimationTabMessageDefinitions);
  const toast = useToast();
  const [searchText, setSearchText] = useState('');

  const {
    isLoading,
    data,
    getResourceEstimationItemsTableRecordsByParent: getEstimationItemsTableRecordsByParent,
  } = useResourceEstimationItemsTableRecords(estimationId, { shouldFetchResources: false });

  const [shouldResetForm, setShouldResetForm] = useState<boolean>(false);
  const [estimationItemIdForAssumptionsModal, setEstimationItemIdForAssumptionsModal] =
    useState<string>();
  const [estimationItemIdForCommentsModal, setEstimationItemIdForCommentsModal] =
    useState<string>();
  const [estimationItemIdsForDetailsModal, setEstimationItemIdsForDetailsModal] = useState<
    string[]
  >([]);
  const [
    estimationGroupIdForCustomEstimationItemCreationModal,
    setEstimationGroupIdForCustomEstimationItemCreationModal,
  ] = useState<string | null>();

  const {
    useCreateEstimationItem,
    useUpdateEstimationItem,
    useRenameEstimationItem,
    usePatchEstimationItems,
    useDeleteEstimationItem,
  } = useEstimationItemQueryActions();
  const createEstimationItemMutation = useCreateEstimationItem();
  const updateEstimationItemMutation = useUpdateEstimationItem();
  const renameEstimationItemMutation = useRenameEstimationItem();
  const patchEstimationItemsMutation = usePatchEstimationItems();
  const deleteEstimationItemMutation = useDeleteEstimationItem();

  const hasEditPermission = useHasEstimationPermission(estimationId, 'CAN_EDIT');

  const topLevelEstimationItems = useMemo(
    () => getEstimationItemsTableRecordsByParent(undefined),
    [getEstimationItemsTableRecordsByParent],
  );

  const recalculate = useMemo(
    () =>
      data?.estimationModel
        ? prepareRecalculate(
            data.estimationItems,
            data.estimationModel,
            data.methodologies,
            data.estimation.methodologyId,
          )
        : undefined,
    [data],
  );

  const onFormReset = useCallback(() => {
    setShouldResetForm(false);
  }, []);

  const exportProps = useMemo<OmitSafely<EstimationExportButtonsProps, 'parentEstimationItemId'>>(
    () => ({
      estimationId,
      title: `${sidebarMessages.detailedEstimation()} - ${tabMessages.estimationLabel()}`,
      outputOptionsArray: [
        {
          showResourcePurposeEstimationItems: true,
          showPhaseLeadColumn: true,
          showMethodologyColumn: true,
          showParameterColumns: true,
          showEstimateColumn: true,
        },
      ],
    }),
    [estimationId, sidebarMessages, tabMessages],
  );

  const estimationExporter = useEstimationExport(exportProps);

  if (isLoading) {
    return <Loader />;
  }

  if (data === undefined) {
    return <Loader />;
  }

  if (!recalculate) {
    throw new Error('This should never happen here');
  }

  const isReadOnly =
    data.estimation.stage === ApiEstimationStage.IN_REVIEW ||
    data.estimation.stage === ApiEstimationStage.APPROVED ||
    !hasEditPermission;

  const onAssumptionsClicked: EstimationItemActionCallback = setEstimationItemIdForAssumptionsModal;
  const onCommentsClicked: EstimationItemActionCallback = setEstimationItemIdForCommentsModal;
  const onDetailsClicked: EstimationItemActionCallback = (estimationItemId: string) => {
    setShouldResetForm(true);
    setEstimationItemIdsForDetailsModal([...estimationItemIdsForDetailsModal, estimationItemId]);
  };
  const onAddPhaseClicked: EstimationGroupActionCallback =
    setEstimationGroupIdForCustomEstimationItemCreationModal;

  const onItemRename = async (estimationItemId: string, newName: string) => {
    await renameEstimationItemMutation.mutateAsync({ estimationItemId, name: newName });
  };

  const tableProps: OmitSafely<
    EstimationItemsGroupedTableProps,
    | 'estimationItems'
    | 'control'
    | 'onIncludeClicked'
    | 'calculatedResultByEstimationItemId'
    | 'searchText'
    | 'formModelEstimationItems'
  > = {
    estimationModel: data.estimationModel,
    methodologies: data.methodologies,
    estimationUnit: data.estimation.unit,
    showButtons: true,
    showMethodology: true,
    showResponsible: true,
    showParameters: true,
    isReadOnly,
    onAssumptionsClicked,
    onCommentsClicked,
    onDetailsClicked,
    onAddPhaseClicked,
    onItemRename,
    showEstimateColumn: true,
  };

  // TODO: this is a workaround until we implement postponed adding custom sub-phase after submitting form in detail modal
  const { onAddPhaseClicked: _unusedOnAddPhaseClicked, ...detailTableProps } = tableProps;

  const onSubmit = async (
    formModel: EstimationItemsEditFormModel,
    removeEstimationItemFromModelById: EstimationItemActionCallback,
  ): Promise<void> => {
    const estimationItemIdsToDelete = formModel.estimationItems
      .filter((x) => x.shouldDelete)
      .map((x) => x.id);

    const formModelItemsToUpdate = formModel.estimationItems
      .filter((x) => !x.shouldDelete)
      .filter((formModelItem) => {
        const responseEstimationItem = topLevelEstimationItems.find(
          (x) => x.id === formModelItem.id,
        );
        if (!responseEstimationItem) {
          throw new Error(`Estimation item id ${formModelItem.id} has not been fetched`);
        }

        const isChanged =
          formModelItem.isIncluded !== !responseEstimationItem.isOmitted ||
          (!responseEstimationItem.hasEstimationModelItemParameters &&
            (formModelItem.methodologyId ?? undefined) !==
              (responseEstimationItem.methodology?.id ?? data.estimation.methodologyId)) ||
          (formModelItem.omitReason ?? undefined) !== responseEstimationItem.omitReason ||
          // We have to filter out parameters that have no parameterName as there's a hard to simulate bug
          // related to react-hook-form that during form reset if size of parameters collection changes to smaller,
          // some stale incomplete items might stay there.
          formModelItem.parameters.filter((x) => !!x.parameterName).length !==
            responseEstimationItem.params.length ||
          formModelItem.parameters
            .filter((x) => !!x.parameterName)
            .some(
              (formModelItemParameter, index) =>
                (formModelItemParameter.value ?? undefined) !==
                responseEstimationItem.params[index].value,
            );

        return isChanged;
      });

    if (estimationItemIdsToDelete.length > 0) {
      for (const estimationItemIdToDelete of estimationItemIdsToDelete) {
        removeEstimationItemFromModelById(estimationItemIdToDelete);
        await deleteEstimationItemMutation.mutateAsync({
          estimationItemId: estimationItemIdToDelete,
        });
      }
    }
    if (formModelItemsToUpdate.length > 0) {
      for (const formModelItemToUpdate of formModelItemsToUpdate) {
        const estimationItemResponse = data.estimationItems.find(
          (x) => x.id === formModelItemToUpdate.id,
        )!;
        const estimationItemUpdatePayload =
          estimationItemUpdateRequestMapper.mergeResponseWithFormModelUpdates(
            estimationItemResponse,
            formModelItemToUpdate,
          );
        await updateEstimationItemMutation.mutateAsync({
          estimationItemId: formModelItemToUpdate.id,
          estimationItem: estimationItemUpdatePayload,
        });
      }
    }
  };

  const onSubmitDetailsModalForm = async (
    formModel: EstimationItemsEditFormModel,
    removeEstimationItemFromModelById: EstimationItemActionCallback,
  ) => {
    if (!formModel.mainEstimationItem) {
      throw new Error('Field mainEstimationItem cannot be undefined.');
    }

    const mainEstimationItemId = formModel.mainEstimationItem.id;

    const estimationItemIdsToDelete = formModel.estimationItems
      .filter((formModelEstimationItem) => formModelEstimationItem.shouldDelete)
      .map((formModelEstimationItem) => formModelEstimationItem.id);

    estimationItemIdsToDelete.forEach((estimationItemIdToDelete) => {
      removeEstimationItemFromModelById(estimationItemIdToDelete);
    });

    const subItemsToUpdate = formModel.estimationItems
      .filter((x) => !x.shouldDelete && !isTemporaryId(x.id))
      .map((formModelEstimationItem) => {
        const estimationItemResponse = data.estimationItems.find(
          (x) => x.id === formModelEstimationItem.id,
        )!;
        return {
          ...estimationItemUpdateRequestMapper.mergeResponseWithFormModelUpdates(
            estimationItemResponse,
            formModelEstimationItem,
          ),
          id: formModelEstimationItem.id,
        };
      });

    const subItemsToCreate = formModel.estimationItems
      .filter((x) => !x.shouldDelete && isTemporaryId(x.id))
      .map<CreateEstimationItemRequest>((formModelEstimationitem) => ({
        name: formModelEstimationitem.name,
        description: formModelEstimationitem.description,
        estimationModelGroupId: formModelEstimationitem.groupId,
        isParentBasedCalculation: true,
        purpose: 'RESOURCE',
        status: 'ESTIMATED',
        unit: data.estimation.unit,
        currencyId: null,
        estimationModelItemId: null,
        methodologyId: formModelEstimationitem.methodologyId,
        parentId: mainEstimationItemId,
        phaseLeadId: formModelEstimationitem.phaseLeadId,
        omitReason: null,
        parameterValues: formModelEstimationitem.parameters.map(
          (formModelItemParam) => formModelItemParam.value,
        ),
      }));

    /**
     * Handle main estimation item from popup
     */
    const estimationItem = data.estimationItems.find((x) => x.id === mainEstimationItemId);
    if (!estimationItem) {
      throw new Error('Could not find estimation item');
    }
    // recreating parameters when methodology changed
    let parameterValues = estimationItem.parameters.map((x) => x.value ?? null);
    if (
      formModel.mainEstimationItem.methodologyId !== (estimationItem.methodologyId ?? undefined)
    ) {
      // The collection only holds the methodologies the estimation model links, and an item can
      // reference another one (EWM-605). An unresolvable methodology leaves the parameters empty
      // instead of taking the whole tab down.
      const methodologyId =
        formModel.mainEstimationItem.methodologyId || data.estimation.methodologyId;
      parameterValues =
        data.methodologies.find((x) => x.id === methodologyId)?.defaultParameters.map(() => 0) ??
        [];
    }

    const mainItemToUpdate: UpdateEstimationItemWithIdRequest = {
      ...estimationItemUpdateRequestMapper.mapFromEstimationItemResponse(estimationItem),
      name: formModel.mainEstimationItem.name,
      description: formModel.mainEstimationItem.description,
      methodologyId: formModel.mainEstimationItem.methodologyId,
      parameterValues: parameterValues,
      phaseLeadId: formModel.mainEstimationItem.phaseLeadId,
      isParentBasedCalculation: formModel.estimationItems.some((subItem) => {
        // Check if there are any subitems with entered values
        return (
          subItem.isIncluded &&
          !subItem.shouldDelete &&
          subItem.parameters.some((param) => (param.value ?? 0) > 0)
        );
      })
        ? false
        : formModel.mainEstimationItem.isParentBasedCalculation,
      id: estimationItem.id,
    };

    await patchEstimationItemsMutation.mutateAsync({
      estimationId,
      payload: {
        estimationItemsToDelete: estimationItemIdsToDelete,
        estimationItemsToCreate: subItemsToCreate,
        // It is important to change main item first, because it can change also sub-items' methodologies on BE.
        // Otherwise, it would crash on attempt of changing methodology of sub-item directly, which is forbidden.
        estimationItemsToUpdate: [mainItemToUpdate, ...subItemsToUpdate],
      },
    });
    setShouldResetForm(true);
    toast.showToast(
      tabMessages.modalSavedMessage({ mainEstimationItemName: estimationItem.name }),
      'success',
    );
  };

  return (
    <Layout>
      <div className='mt-4 flex justify-end gap-2 rounded-xl'>
        <Search handleOnChange={setSearchText} />
        <ExportButtonsSelector
          exportConfiguration={[
            eExportFormat.EXCEL,
            eExportFormat.PDF,
            eExportFormat.CSV,
            eExportFormat.JSON,
            eExportFormat.PRINT,
          ]}
          onExportButtonClick={(format) => estimationExporter(format, undefined)}
        />
      </div>
      <TopLevelEstimationItemsForm
        topLevelEstimationItems={topLevelEstimationItems}
        methodologies={data.methodologies}
        estimationMethodologyId={data.estimation.methodologyId}
        tableProps={{ ...tableProps, searchText }}
        onSubmit={onSubmit}
        recalculate={recalculate}
      />
      {estimationItemIdsForDetailsModal.map((estimationItemIdForDetail) => (
        <EstimationItemDetailModal
          key={estimationItemIdForDetail}
          open={!!estimationItemIdForDetail}
          onClose={() =>
            setEstimationItemIdsForDetailsModal([...estimationItemIdsForDetailsModal.slice(0, -1)])
          }
          onSubmit={onSubmitDetailsModalForm}
          shouldResetForm={shouldResetForm}
          onFormReset={onFormReset}
          estimationItem={
            estimationItemIdForDetail === undefined
              ? undefined
              : getResourceEstimationItemDetail(estimationItemIdForDetail, data)
          }
          estimationItems={
            estimationItemIdForDetail
              ? getEstimationItemsTableRecordsByParent(estimationItemIdForDetail)
              : emptyArray
          }
          estimationMethodologyId={data.estimation.methodologyId}
          estimationCultureName={data.estimation.cultureName}
          recalculate={recalculate}
          exportProps={exportProps}
          {...detailTableProps}
        />
      ))}
      <AssumptionsModal
        estimationId={estimationId}
        onClose={() => setEstimationItemIdForAssumptionsModal(undefined)}
        estimationItem={data.estimationItems.find(
          (x) => x.id === estimationItemIdForAssumptionsModal,
        )}
        isReadOnlyForced={isReadOnly}
      />
      <CustomEstimationItemCreationModal
        estimationModel={data.estimationModel}
        defaultMethodologyId={data.estimation.methodologyId}
        canChangeMethodology={true}
        defaultGroupId={estimationGroupIdForCustomEstimationItemCreationModal ?? undefined}
        estimationCultureName={data.estimation.cultureName}
        open={estimationGroupIdForCustomEstimationItemCreationModal !== undefined}
        parentItemOptions={data.estimationItems
          .filter((item) => !item.parentId && item.purpose === ApiEstimationItemPurpose.RESOURCE)
          .map((item) => ({ value: item.id, label: item.name }))}
        onClose={() => setEstimationGroupIdForCustomEstimationItemCreationModal(undefined)}
        onSave={(formModel) => {
          if (!formModel.groupId) {
            throw new Error('Group must be chosen');
          }
          const methodology = data.methodologies.find((x) => x.id === formModel.methodologyId);
          if (!methodology) {
            throw new Error('Methodology must be chosen');
          }
          const estimationItemCreatePayload: CreateEstimationItemRequest = {
            name: formModel.name,
            description: formModel.description,
            estimationModelGroupId: formModel.groupId,
            isParentBasedCalculation: true,
            methodologyId: formModel.methodologyId,
            status: 'ESTIMATED',
            unit: data.estimation.unit,
            purpose: 'RESOURCE',
            parentId: formModel.parentId ?? undefined,
            phaseLeadId: formModel.phaseLeadId,
            parameterValues: methodology.defaultParameters.map(() => null),
          };
          createEstimationItemMutation.mutate({
            estimationId: data.estimation.id,
            estimationItem: estimationItemCreatePayload,
          });
          setEstimationGroupIdForCustomEstimationItemCreationModal(undefined);
        }}
      />
      <CommentsModal
        commentType='ESTIMATION_ITEM'
        commentContext='ESTIMATION'
        size='s'
        estimationId={data.estimation.id}
        estimationItem={data.estimationItems.find((x) => x.id === estimationItemIdForCommentsModal)}
        onClose={() => setEstimationItemIdForCommentsModal(undefined)}
      />
    </Layout>
  );
};

export default EstimationDetailTab;
